import citiesRaw from '../data/cities.json'
import { countryName } from './countryNames'

export interface CityRecord {
  city: string
  region: string
  country: string
  lat: number
  lng: number
  pop: number
}

const cities = citiesRaw as CityRecord[]

/**
 * Returns the canonical city label that is stored in the `city` column.
 *
 * Format:
 *   US  → "City, ST"          (2-letter state from region, uppercased)
 *   non-US → "City, CountryName"  (full English country name from the BUNDLED
 *             countryName map; falls back to the ISO code for unknown codes)
 *
 * Uses the BUNDLED country-name map (not runtime Intl.DisplayNames) so the admin
 * panel (V8) and mobile app (Hermes) produce BYTE-IDENTICAL labels for the same
 * city regardless of each runtime's ICU data.
 */
export function cityLabel(c: CityRecord): string {
  if (c.country === 'US') {
    return `${c.city}, ${c.region.toUpperCase()}`
  }
  return `${c.city}, ${countryName(c.country)}`
}

/**
 * Normalise a query string: NFD decompose → strip combining diacritics →
 * lowercase → trim → collapse internal whitespace. Kept consistent with the
 * mobile `normCity` so both surfaces match search input identically.
 */
function normalise(str: string): string {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

// ── Trie (built once at module load; mirrors mobile/src/lib/geo/citySearch.ts) ──

interface TrieNode {
  children: Map<string, TrieNode>
  records: CityRecord[]
}

function makeNode(): TrieNode {
  return { children: new Map(), records: [] }
}

function trieInsert(root: TrieNode, key: string, record: CityRecord): void {
  let node = root
  for (const ch of key) {
    let child = node.children.get(ch)
    if (!child) {
      child = makeNode()
      node.children.set(ch, child)
    }
    node = child
  }
  node.records.push(record)
}

function collectAll(node: TrieNode, out: CityRecord[]): void {
  for (const r of node.records) out.push(r)
  for (const child of node.children.values()) collectAll(child, out)
}

const TRIE_ROOT: TrieNode = makeNode()
for (const entry of cities) {
  const key = normalise(entry.city)
  trieInsert(TRIE_ROOT, key, entry)
  // Index each subsequent word boundary so "new" → "New York" / "New Orleans".
  const words = key.split(' ')
  for (let i = 1; i < words.length; i++) {
    trieInsert(TRIE_ROOT, words.slice(i).join(' '), entry)
  }
}

/**
 * Returns up to `limit` cities whose normalised name (or any word within it)
 * starts with the normalised query, ranked by population descending.
 * O(k) trie walk + bounded sort. Returns [] for an empty query.
 */
export function searchCities(query: string, limit = 10): CityRecord[] {
  const q = normalise(query)
  if (!q) return []

  let node: TrieNode | undefined = TRIE_ROOT
  for (const ch of q) {
    node = node.children.get(ch)
    if (!node) return []
  }

  const candidates: CityRecord[] = []
  collectAll(node, candidates)

  const seen = new Set<CityRecord>()
  const unique: CityRecord[] = []
  for (const r of candidates) {
    if (!seen.has(r)) {
      seen.add(r)
      unique.push(r)
    }
  }

  unique.sort((a, b) => b.pop - a.pop || a.city.localeCompare(b.city))
  return unique.slice(0, limit)
}
